/*
  Inspired by Repo below.
  https://github.com/drac94/react-auth-code-input
 */

import React, { useRef, useEffect, type JSX } from 'react';

interface Props {
  allowedCharacters?: 'alpha' | 'numeric' | 'alphanumeric';
  ariaLabel?: string;
  length?: number;
  containerId?: string;
  inputClassName?: string;
  isPassword?: boolean;
  onChange: (res: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  capitalize?: boolean;
}

type InputMode = 'text' | 'numeric';

type InputType = 'text' | 'number' | 'password';

interface InputProps {
  type: InputType;
  inputMode: InputMode;
  pattern: string;
  min?: string;
  max?: string;
}

const propsMap: Record<string, InputProps> = {
  alpha: {
    type: 'text',
    inputMode: 'text',
    pattern: '[a-zA-Z]{1}',
  },

  alphanumeric: {
    type: 'text',
    inputMode: 'text',
    pattern: '[a-zA-Z0-9]{1}',
  },

  numeric: {
    type: 'number',
    inputMode: 'numeric',
    pattern: '[0-9]{1}',
    min: '0',
    max: '9',
  },
};

const AuthCode = (
  {
    allowedCharacters = 'alphanumeric',
    capitalize = false,
    ariaLabel,
    length = 6,
    containerId,
    inputClassName,
    isPassword = false,
    onChange,
    onKeyDown,
  }: Props
): JSX.Element => {
  const inputsRef = useRef<HTMLInputElement[]>([]);

  const inputProps = propsMap[allowedCharacters];

  useEffect(() => {
    inputsRef.current[0].focus();
  }, []);

  const sendResult = (): void => {
    const res = inputsRef.current.map((input) => input.value).join('');
    if (onChange) {
      onChange(res);
    }
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const {
      target: { value, nextElementSibling },
    } = e;
    if (capitalize) {
      e.target.value = e.target.value.toUpperCase();
    }
    if (value.length > 1) {
      e.target.value = value.charAt(0);
      if (nextElementSibling !== null) {
        (nextElementSibling as HTMLInputElement).focus();
      }
    } else {
      if (value.match(inputProps.pattern)) {
        if (nextElementSibling !== null) {
          (nextElementSibling as HTMLInputElement).focus();
        }
      } else {
        e.target.value = '';
      }
    }
    sendResult();
  };

  const handleOnInput = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    const target = e.target as HTMLInputElement;
    if (target.value.length === 1) {
      if (target.nextElementSibling !== null) {
        e.preventDefault();
        (target.nextElementSibling as HTMLInputElement).focus();
      }
      sendResult();
    }
  };

  // eslint-disable-next-line complexity
  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    const { key } = e;
    const target = e.target as HTMLInputElement;
    if (key === 'Backspace') {
      if (target.value === '' && target.previousElementSibling !== null) {
        (target.previousElementSibling as HTMLInputElement).focus();
        (target.previousElementSibling as HTMLInputElement).value = '';
        e.preventDefault();
      } else {
        e.preventDefault();
        target.value = '';
      }
      sendResult();
    }
    if (key === 'ArrowLeft') {
      if (!target.previousElementSibling) {
        e.preventDefault();
      }
      if (target.previousElementSibling !== null) {
        e.preventDefault();
        (target.previousElementSibling as HTMLInputElement).focus();
      }
      sendResult();
    }
    if (key === 'ArrowRight') {
      if (!target.nextElementSibling) {
        e.preventDefault();
      }
      if (target.nextElementSibling !== null) {
        e.preventDefault();
        (target.nextElementSibling as HTMLInputElement).focus();
      }
      sendResult();
    }
    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
    e.target.select();
  };

  const handleOnPaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
    let pastedValue = e.clipboardData.getData('Text');

    if (capitalize) {
      pastedValue = pastedValue.toUpperCase();
    }
    let currentInput = 0;

    for (let i = 0; i < pastedValue.length; i++) {
      const pastedCharacter = pastedValue.charAt(i);
      const currentValue = inputsRef.current[currentInput].value;
      if (pastedCharacter.match(inputProps.pattern)) {
        if (!currentValue) {
          inputsRef.current[currentInput].value = pastedCharacter;
          // eslint-disable-next-line max-depth
          if (inputsRef.current[currentInput].nextElementSibling !== null) {
            (inputsRef.current[currentInput]
              .nextElementSibling as HTMLInputElement).focus();
            currentInput++;
          }
        }
      }
    }
    sendResult();

    e.preventDefault();
  };

  const inputs = [];
  for (let i = 0; i < length; i++) {
    inputs.push(
      <input
        key={i}
        onChange={handleOnChange}
        onKeyDown={handleOnKeyDown}
        onFocus={handleOnFocus}
        onPaste={handleOnPaste}
        onInput={handleOnInput}
        {...inputProps}
        type={isPassword ? 'password' : inputProps.type}
        ref={(el: HTMLInputElement): HTMLInputElement => (inputsRef.current[i] = el)}
        maxLength={1}
        className={inputClassName}
        autoComplete={i === 0 ? 'one-time-code' : 'off'}
        aria-label={
          ariaLabel
            ? `${ariaLabel}. Character ${i + 1}.`
            : `Character ${i + 1}.`
        }
      />
    );
  }

  return <fieldset id={containerId}>{inputs}</fieldset>;
};

export default AuthCode;
